<template>
	<Teleport to="body">
		<aside :class="['vcx-fly-out', { 'vcx-fly-out--active': modelValue }]" data-test="vcx-fly-out">
			<!-- Backdrop -->
			<div class="vcx-fly-out__backdrop" aria-hidden="true" data-test="vcx-fly-out-backdrop" @click="close"></div>

			<!-- Drawer -->
			<div
				ref="drawerRef"
				role="dialog"
				aria-modal="true"
				aria-label="VCX"
				tabindex="-1"
				:inert="!modelValue || undefined"
				class="vcx-theme-dark vcx-fly-out__drawer vcx-p-150 vcx-pb-300"
			>
				<BaseButton
					type="button"
					classes="vcx-fly-out__close"
					aria-label="Close drawer"
					data-context="VCX Flyout Close"
					@click="close"
				>
					<BaseSvgIcon name="close-light" width="24" height="24" />
				</BaseButton>

				<div class="vcx-fly-out__content vcx-body-lg">
					<div class="vcx-fly-out__brokerages vcx-mb-300">
						<ListItemLink
							v-for="brokerage in brokerages"
							:key="brokerage.name"
							data-context="Flyout Menu"
							:link="getBrokerageLink(brokerage)"
							:list-item-config="{
								listItemSize: 'small',
								heading: brokerage.name,
								showChevron: true
							}"
							class="vcx-brokerage-link"
							@click="close()"
						/>
					</div>
				</div>
			</div>
		</aside>
	</Teleport>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watchEffect } from 'vue';
import ListItemLink from '@components/form/list-item/list-item-link.vue';
import { vcxBrokerages } from '@constants/vcx';
import type { Link } from 'types/layout';
import type { VcxBroker } from 'types/vcx';

interface Props {
	modelValue: boolean;
}

interface Emits {
	(e: 'update:modelValue', value: boolean): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const brokerages: Array<VcxBroker> = vcxBrokerages;
const drawerRef = ref<HTMLElement | null>(null);
const previousActiveElement = ref<HTMLElement | null>(null);

function getBrokerageLink(brokerage: VcxBroker): Link {
	const url = new URL(brokerage.url);
	url.searchParams.set('utm_source', 'fundrise');

	return {
		text: brokerage.name,
		href: url.toString(),
		rel: 'noopener noreferrer',
		target: '_blank'
	};
}

function close() {
	emit('update:modelValue', false);
}

watchEffect(async () => {
	const isOpen = props.modelValue;
	if (isOpen) {
		previousActiveElement.value = document.activeElement as HTMLElement;
		document.body.style.overflow = 'hidden';

		await nextTick();
		drawerRef.value?.focus();
	} else {
		document.body.style.overflow = '';
		const prev = previousActiveElement.value;
		if (prev && prev !== document.body) {
			prev.focus();
		} else {
			(document.activeElement as HTMLElement | null)?.blur();
		}
	}
	return () => {
		document.body.style.overflow = '';
	};
});

function handleEscape(event: KeyboardEvent) {
	if (event.key === 'Escape' && props.modelValue) {
		close();
	}
}

onMounted(() => {
	document.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
	document.removeEventListener('keydown', handleEscape);
	document.body.style.overflow = '';
});
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive.scss' as *;

.vcx-fly-out {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 1000;
	pointer-events: none;

	&--active {
		pointer-events: auto;

		.vcx-fly-out__backdrop {
			opacity: 1;
		}

		.vcx-fly-out__drawer {
			transform: translateX(0);
			outline: none;
		}
	}

	&__backdrop {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.7);
		opacity: 0;
		transition: opacity var(--vcx-transition-base);
		cursor: pointer;
	}

	&__drawer {
		overflow-y: auto;
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		width: 100%;
		max-width: 100%;
		background-color: var(--vcx-bg-dark);
		transform: translateX(100%);
		transition: transform var(--vcx-transition-base);
		display: flex;
		gap: var(--vcx-space-200);
		flex-direction: column;
		box-shadow: var(--vcx-shadow-lg);

		@include vcx-media-md {
			max-width: 480px;
		}
	}

	&__close {
		color: var(--vcx-content-inverse);
		transition: opacity var(--vcx-transition-fast);
		margin: 0 0 0 auto;

		&:hover {
			opacity: 0.8;
		}

		&:focus-visible {
			outline: 1px solid var(--vcx-brand-primary);
			outline-offset: 1px;
		}
	}

	&__brokerages {
		display: flex;
		flex-direction: column;
		gap: var(--vcx-space-100);
	}

	.vcx-brokerage-link {
		border: 1px solid var(--vcx-gray-40);
		border-radius: var(--vcx-radius-25);
		color: var(--vcx-gray-white);

		:deep(.list-item__heading),
		:deep(svg) {
			color: var(--vcx-gray-white);
		}

		&:hover {
			border-color: var(--vcx-brand-primary);

			:deep(svg) {
				color: var(--vcx-brand-primary);
			}
		}
	}
}
</style>
